// Section 16 (inequality) and Section 20 (fragility index). Pure functions over a context.
import { clamp, gini, mean } from '../../core/math';
import { memoByState } from '../../core/memo';
import { peopleSorted, settlementTrust } from '../../core/queries';
import { sortedValues } from '../../core/sorted';
import type { Factor, GameState, ReadContext, SeasonReport } from '../../core/types';
import { num } from './value';

/** Gini of food received relative to need, per person (Section 16). */
export function foodGini(report: SeasonReport): number {
  return gini(Object.values(report.needs).map((n) => n.food));
}

/** Gini of beds per member across households (Section 16). */
export function bedsGini(state: GameState, report: SeasonReport): number {
  const values: number[] = [];
  for (const h of sortedValues(state.households)) {
    const size = h.memberIds.length;
    if (size === 0) continue;
    const beds = h.buildingId === null ? 0 : size - (report.crowding[h.id] ?? 0);
    values.push(beds / size);
  }
  return gini(values);
}

/** Jobs among the Section 20 knowledge skills with at most one person at skill 3+. */
export function thinKnowledge(ctx: ReadContext): number {
  const people = peopleSorted(ctx.state);
  let thin = 0;
  for (const skill of ctx.cfg.steady.knowledgeSkills) {
    const holders = people.filter(
      (p) => (p.skills[skill] ?? 0) >= ctx.cfg.steady.knowledgeMinSkill,
    ).length;
    if (holders <= 1) thin++;
  }
  return thin;
}

/** Mean illness multiplier over the population (illness risk / base). */
export function illnessMultiplier(ctx: ReadContext): number {
  return memoByState(ctx.state, 'illness-multiplier', () => {
    const people = peopleSorted(ctx.state);
    if (people.length === 0) return 1;
    return mean(
      people.map(
        (p) => num(ctx, { kind: 'illnessRisk', personId: p.id }) / ctx.cfg.illness.baseChance,
      ),
    );
  });
}

/** Section 20: fragility 0..100 as a weighted sum of normalised terms, with its factor rows. */
export function fragility(
  ctx: ReadContext,
  report: SeasonReport,
  /** Food plus seed on hand now (the caller may be mid-phase, after spoilage). */
  foodOnHand: number,
): { value: number; factors: Factor[] } {
  const f = ctx.cfg.fragility;
  const state = ctx.state;
  const people = peopleSorted(state);
  const pop = people.length;
  const perSeason =
    Object.values(report.demand.food ?? {}).reduce((a, b) => a + b, 0) ||
    pop * ctx.cfg.needs.foodAdult;
  const seasonsOfFood = perSeason > 0 ? foodOnHand / perSeason : 0;
  const safetyNeeded = pop / ctx.cfg.threats.peoplePerSafetyPoint;
  const safety = report.services.safetyPoints ?? 0;
  const terms: [string, number, number][] = [
    ['food', f.foodWeight, clamp(1 - seasonsOfFood / f.foodSeasonsSafe, 0, 1)],
    [
      'weakness',
      f.weaknessWeight,
      pop === 0 ? 1 : people.filter((p) => p.strength < f.weakStrengthBelow).length / pop,
    ],
    ['illness', f.illnessWeight, clamp((report.illnessMultiplier - 1) / f.illnessSpan, 0, 1)],
    ['safety', f.safetyWeight, safetyNeeded <= 0 ? 0 : clamp(1 - safety / safetyNeeded, 0, 1)],
    ['trust', f.trustWeight, clamp((f.trustSafe - settlementTrust(state)) / f.trustSafe, 0, 1)],
    ['knowledge', f.knowledgeWeight, thinKnowledge(ctx) / ctx.cfg.steady.knowledgeSkills.length],
  ];
  const factors: Factor[] = terms.map(([name, weight, term]) => ({
    source: `fragility:${name}`,
    op: 'add',
    value: weight * term,
    delta: weight * term,
    note: `${Math.round(term * 100)}% of ${weight}`,
  }));
  return {
    value: clamp(
      factors.reduce((a, r) => a + r.delta, 0),
      0,
      100,
    ),
    factors,
  };
}
